import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { 
  Search, Filter, AlertTriangle, Users, Server, Bot, Key, 
  ChevronRight, Activity 
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getAgents } from '../services/api';

const InventoryPage = () => {
  const navigate = useNavigate();
  const { stats } = useOutletContext();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');
  const [envFilter, setEnvFilter] = useState('all');

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await getAgents();
      setAgents(response.data);
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAgents = agents.filter((agent) => {
    const matchesSearch = agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || agent.status === statusFilter;
    const matchesRisk = riskFilter === 'all' || agent.risk_level === riskFilter;
    const matchesEnv = envFilter === 'all' || agent.environment === envFilter;
    return matchesSearch && matchesStatus && matchesRisk && matchesEnv;
  });

  const getTypeIcon = (type) => {
    switch (type) {
      case 'ai_agent': return <Bot className="w-4 h-4 text-[#0066FF]" />;
      case 'service_account': return <Server className="w-4 h-4 text-[#34C759]" />;
      case 'bot': return <Activity className="w-4 h-4 text-[#FFCC00]" />;
      case 'token': return <Key className="w-4 h-4 text-[#FF6961]" />;
      default: return <Users className="w-4 h-4 text-[#9CA3AF]" />;
    }
  };

  const getRiskClass = (level) => {
    switch (level) {
      case 'critical': return 'risk-critical';
      case 'high': return 'risk-high';
      case 'medium': return 'risk-medium';
      default: return 'risk-low';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'active': return 'status-active';
      case 'disabled': return 'status-disabled';
      case 'blocked': return 'status-blocked';
      default: return 'text-[#FFCC00] bg-[#FFCC00]/10 px-2 py-0.5 rounded-none text-xs font-mono';
    }
  };

  return (
    <div data-testid="inventory-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#F9FAFB] mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Agent Inventory
        </h1>
        <p className="text-[#9CA3AF]">
          Discover and manage all AI agents and non-human identities in your enterprise.
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="enterprise-card p-6">
            <div className="stat-value text-[#0066FF]">{stats.total_agents}</div>
            <div className="stat-label">Total Identities</div>
          </div>
          <div className="enterprise-card p-6">
            <div className="stat-value text-[#34C759]">{stats.active_agents}</div>
            <div className="stat-label">Active</div>
          </div>
          <div className="enterprise-card p-6">
            <div className="stat-value text-[#FF3B30]">{stats.identities_without_owner}</div>
            <div className="stat-label">No Owner</div>
          </div>
          <div className="enterprise-card p-6">
            <div className="stat-value text-[#FFCC00]">{stats.excessive_privileges}</div>
            <div className="stat-label">Excessive Privileges</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="enterprise-card p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
            <Input
              placeholder="Search agents by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none"
              data-testid="search-agents-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
              <SelectItem value="approval_required">Approval Required</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
            </SelectContent>
          </Select>
          <Select value={riskFilter} onValueChange={setRiskFilter}>
            <SelectTrigger className="w-[160px] bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none" data-testid="risk-filter">
              <SelectValue placeholder="Risk Level" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
              <SelectItem value="all">All Risk</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
          <Select value={envFilter} onValueChange={setEnvFilter}>
            <SelectTrigger className="w-[160px] bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none" data-testid="env-filter">
              <SelectValue placeholder="Environment" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
              <SelectItem value="all">All Environments</SelectItem>
              <SelectItem value="production">Production</SelectItem>
              <SelectItem value="staging">Staging</SelectItem>
              <SelectItem value="development">Development</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Agents Table */}
      <div className="enterprise-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#9CA3AF]">Loading agents...</div>
        ) : filteredAgents.length === 0 ? (
          <div className="p-8 text-center text-[#9CA3AF]">No agents found matching your criteria.</div>
        ) : (
          <table className="data-table" data-testid="agents-table">
            <thead>
              <tr className="bg-[#1A1A1A]">
                <th>Agent Name</th>
                <th>Type</th>
                <th>Owner</th>
                <th>Environment</th>
                <th>Tools</th>
                <th>Critical Privs</th>
                <th>Risk</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredAgents.map((agent) => (
                <tr 
                  key={agent.id} 
                  className="cursor-pointer"
                  onClick={() => navigate(`/dashboard/agents/${agent.id}`)}
                  data-testid={`agent-row-${agent.id}`}
                >
                  <td>
                    <div className="flex items-center gap-3">
                      {getTypeIcon(agent.type)}
                      <div>
                        <div className="text-[#F9FAFB] font-medium">{agent.name}</div>
                        <div className="text-xs text-[#6B7280]">{agent.framework || agent.source_connector}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-[#9CA3AF] uppercase text-xs">{agent.type.replace('_', ' ')}</td>
                  <td>
                    {agent.owner ? (
                      <span className="text-[#9CA3AF]">{agent.owner}</span>
                    ) : (
                      <span className="flex items-center gap-1 text-[#FF3B30]">
                        <AlertTriangle className="w-3 h-3" />
                        No Owner
                      </span>
                    )}
                  </td>
                  <td className="text-[#9CA3AF]">{agent.environment}</td>
                  <td className="text-[#0066FF] font-mono">{agent.tools_count}</td>
                  <td className="font-mono">
                    <span className={agent.critical_privileges > 5 ? 'text-[#FF3B30]' : 'text-[#9CA3AF]'}>
                      {agent.critical_privileges}
                    </span>
                  </td>
                  <td>
                    <span className={getRiskClass(agent.risk_level)}>{agent.risk_level}</span>
                  </td>
                  <td>
                    <span className={getStatusClass(agent.status)}>{agent.status}</span>
                  </td>
                  <td>
                    <ChevronRight className="w-4 h-4 text-[#6B7280]" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default InventoryPage;
